import { Link } from "react-router-dom";
import { ShieldCheck, Star, Award, Users, BadgeCheck, ArrowRight } from "lucide-react";

/**
 * Reusable "Why Choose Anand Tutorials" EEAT block — used on Home, all City pages,
 * Board/Exam course pages and City-Topic landing pages.
 *
 * Props let the host page contextualise the heading (e.g. "across Jabalpur" / "for CBSE")
 * without breaking the consistent EEAT framing search engines reward.
 */
export default function WhyChooseAnandTutorials({
    eyebrow = "Why parents pick us",
    headline = "Why choose Anand Tutorials.",
    contextLabel = "across India",
    showAuthorityLink = true,
}) {
    const points = [
        {
            icon: ShieldCheck,
            title: "Verified, police-checked tutors",
            body: "Every tutor passes a 4-step screening — academic credentials, subject test, teaching demo and police background check. Roughly 1 in 5 applicants makes it onto our live panel.",
        },
        {
            icon: Users,
            title: "True 1-on-1 — never a batch",
            body: "Every Anand Tutorials class is one tutor with one student. No 80-kid coaching halls, no 'small group' cover, no algorithmic next-video personalisation. Real teaching needs a real teacher.",
        },
        {
            icon: BadgeCheck,
            title: "12+ years, 50,000+ students",
            body: "Founded in 2013 in Jabalpur, we have continuously delivered 1:1 home and online tuition for twelve uninterrupted years. Editorial team, founder review and a public policy framework back every claim.",
        },
        {
            icon: Star,
            title: "4.8/5 across 1,200+ verified reviews",
            body: "Parent ratings collected only after 4+ paid weeks of classes — so the rating cannot be gamed by single-class trolls or seeded reviews. We don't delete one-star reviews.",
        },
        {
            icon: Award,
            title: "Refund-protected, no lock-in",
            body: "Free 1:1 demo class with no card. Money-back refund window if there's a mismatch. Single Customer Care number for accountability — no chatbots, no ticket queues, no lost emails.",
        },
        {
            icon: ShieldCheck,
            title: "Same tutor, full term",
            body: "The tutor you meet at demo continues for the entire term. No tutor reassignment without parent consent. Continuity is the most underrated factor in 1:1 tuition outcomes.",
        },
    ];

    return (
        <section
            data-testid="why-choose-anand-tutorials"
            className="py-16 sm:py-20 bg-gradient-to-b from-white via-[#F8FAFC] to-white"
        >
            <div className="max-w-6xl mx-auto px-6 lg:px-8">
                <div className="text-center max-w-3xl mx-auto mb-10">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-50 border border-amber-200 text-[10px] uppercase tracking-[0.22em] font-bold text-amber-700 mb-4">
                        <Star className="w-3 h-3 fill-amber-500 text-amber-500" /> {eyebrow}
                    </div>
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-[#0F172A] leading-tight">
                        {headline}
                    </h2>
                    <p className="mt-4 text-slate-600 text-base sm:text-lg leading-relaxed">
                        India's most trusted name in 1:1 home and online tuition {contextLabel} — and the trust signals to back every word.
                    </p>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {points.map((p) => {
                        const Icon = p.icon;
                        return (
                            <div
                                key={p.title}
                                className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#1E3A8A]/30 hover:shadow-md transition-all"
                            >
                                <div className="w-10 h-10 rounded-lg bg-[#1E3A8A]/10 text-[#1E3A8A] flex items-center justify-center mb-4">
                                    <Icon className="w-5 h-5" />
                                </div>
                                <h3 className="font-heading font-semibold text-[#0F172A] text-base mb-2 leading-snug">{p.title}</h3>
                                <p className="text-sm text-slate-600 leading-relaxed">{p.body}</p>
                            </div>
                        );
                    })}
                </div>

                {showAuthorityLink && (
                    <div className="text-center mt-10">
                        <Link
                            to="/why-anand-tutorials-is-trusted-across-india"
                            data-testid="why-choose-authority-link"
                            className="inline-flex items-center gap-2 text-sm font-semibold text-[#1E3A8A] hover:text-[#0F172A] transition-colors group"
                        >
                            Read the full trust framework
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </Link>
                    </div>
                )}
            </div>
        </section>
    );
}
